import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import NumberInput from 'further-ui/components/NumberInput';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'further-ui/components/Accordion';
import { ExpandMore } from '@mui/icons-material';
import StepIndex from 'further-ui/components/StepIndex';
import { Chip, TextField, Tooltip } from '@mui/material';
import TooltipButton from 'further-ui/components/TooltipButton';
import {
  LpFeeStartDate,
  LpArrearsCalculation,
} from 'further-types/lp/lp-fee-template';
import Switch from 'further-ui/components/Switch';
import { Row, Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import { InferredFeeSettingsFormType } from './FormWrapper';
import FeeTemplateFeesTable from './FeeTemplateFeesTable';
import { FeeApportionmentBasis } from 'further-types/lp/lp-fee-template';
import { FeesEqualisationTreatment } from 'further-types/lp/equalisation';
import { FeesEqualisationTreatmentLabels } from 'further-ui/labels';

const feeApportionmentBasisOptions = [
  {
    value: FeeApportionmentBasis.OneNumberOfPeriods,
    label: '1/Number of periods',
  },
  { value: FeeApportionmentBasis.ActualActual, label: 'Daily' },
];

const feesEqualisationTreatmentOptions = Object.entries(
  FeesEqualisationTreatmentLabels,
).map(([value, label]) => ({
  value: value as FeesEqualisationTreatment,
  label,
}));

const feeApportionmentBasisTooltip = (
  <>
    <p>
      This defines how much of a fee is accrued in each period. It is calculated
      as follows:
    </p>
    <br />
    <p>
      <b>1/number of periods</b> - This is 1 divided by the{' '}
      <i>Billing frequency</i> period. If the <i>Billing frequency</i> period is
      quarterly, the fee accrues as 1/4th of the annual fee per quarter. The fee
      accrues fully on either the first day of the period (advance) or last day
      of the period (arrears).
    </p>
    <br />
    <p>
      <b>Daily</b> - This calculates the fee on a daily basis and multiplies by
      the number of days in the period (e.g. 90-92 days per quarter for
      quarterly). It is similar to using an Actual/Actual day count convention.
    </p>
  </>
);

const feeStartDateOptions = [
  {
    value: LpFeeStartDate.FundFirstCloseDate,
    label: 'Fund first close date',
  },
  {
    value: LpFeeStartDate.DateOfFirstFundInvestment,
    label: 'Date of first fund investment',
  },
];

const arrearsCalculationOptions = [
  {
    value: LpArrearsCalculation.BasedOnPeriodEndingAmount,
    label: 'Based on period ending amount',
  },
];

const FeeTemplateSection: FC<{
  index: number;
  remove: (index: number) => void;
  hasApprovedFees: boolean;
  hasCommitments: boolean;
  setHasEditedTemplateWithCommitment: (
    hasEditedTemplateWithCommitment: boolean,
  ) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}> = ({
  index,
  remove,
  hasApprovedFees,
  hasCommitments,
  setHasEditedTemplateWithCommitment,
  expanded,
  onExpandedChange,
}) => {
  const { control, setValue, getValues, watch } =
    useFormContext<InferredFeeSettingsFormType>();

  const lpFeeTemplates = getValues('lpFeeTemplates');
  const feeTemplateEqualisationTreatment = watch(
    `lpFeeTemplates.${index}.feesEqualisationTreatment`,
  );
  const isDefaultFeeTemplate = watch(
    `lpFeeTemplates.${index}.isDefaultFeeTemplate`,
  );
  const templateName = watch(`lpFeeTemplates.${index}.name`);

  const handleDefaultTemplateChange = (checked: boolean) => {
    if (checked) {
      lpFeeTemplates.forEach((_, i) => {
        if (i !== index) {
          setValue(`lpFeeTemplates.${i}.isDefaultFeeTemplate`, false, {
            shouldValidate: true,
          });
        }
      });
    }
  };

  const templateInUseMessage = hasApprovedFees
    ? 'This field is disabled because it has approved fees.'
    : undefined;

  const disabledUntilLaterDate = true;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandedChange(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Row justify="space-between" fullWidth>
          <Row centered spacing="sm">
            <StepIndex
              index={index + 1}
              label={templateName || `Core fee template ${index + 1}`}
            />
          </Row>
          {isDefaultFeeTemplate && (
            <Chip color="default" size="small" label="Default template" />
          )}
        </Row>
      </AccordionSummary>
      <AccordionDetails>
        <Column spacing="lg">
          <FormField
            labelSize="md"
            size="md"
            label="Name"
            layout="horizontal"
            breakpoint="md"
          >
            <Controller
              name={`lpFeeTemplates.${index}.name`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  placeholder="Enter fee template name"
                  error={!!error}
                  helperText={error?.message}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            />
          </FormField>
          <FormField
            labelSize="md"
            breakpoint="md"
            size="md"
            label="Default fee template"
            layout="horizontal"
          >
            <Controller
              name={`lpFeeTemplates.${index}.isDefaultFeeTemplate`}
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <Switch
                  name={rest.name}
                  checked={!!rest.value}
                  edge="start"
                  onChange={(checked) => {
                    handleDefaultTemplateChange(checked);
                    onChange(checked);
                  }}
                />
              )}
            />
          </FormField>
          <Controller
            name={`lpFeeTemplates.${index}.feeApportionmentBasis`}
            control={control}
            render={({ fieldState: { error }, field: { value, onChange } }) => (
              <FormField
                tooltip={feeApportionmentBasisTooltip}
                label="Fee accrual"
                breakpoint="md"
                layout="horizontal"
                labelSize="md"
                size="md"
              >
                <Tooltip title={templateInUseMessage ?? ''}>
                  <span>
                    <BasicSelect
                      options={feeApportionmentBasisOptions}
                      placeholder="Select fee accrual type"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      value={value}
                      onChange={onChange}
                      disabled={hasApprovedFees}
                    />
                  </span>
                </Tooltip>
              </FormField>
            )}
          />
          <Controller
            name={`lpFeeTemplates.${index}.feesEqualisationTreatment`}
            control={control}
            render={({ fieldState: { error }, field: { value, onChange } }) => (
              <FormField
                label="Equalisation treatment"
                breakpoint="md"
                layout="horizontal"
                tooltip="The selected equalisation treatment will be applied to historic fees for new investors joining the vehicle after first close. This fee is editable on a per-investor basis in the Close & Equalise tool."
                labelSize="md"
                size="md"
              >
                <Tooltip title={templateInUseMessage ?? ''}>
                  <span>
                    <BasicSelect
                      options={feesEqualisationTreatmentOptions}
                      placeholder="Select equalisation treatment"
                      fullWidth
                      error={!!error}
                      value={value}
                      onChange={onChange}
                      disabled={hasApprovedFees}
                    />
                  </span>
                </Tooltip>
              </FormField>
            )}
          />
          {feeTemplateEqualisationTreatment ===
            FeesEqualisationTreatment.Partial && (
            <FormField
              label="Equalisation treatment percentage"
              breakpoint="md"
              layout="horizontal"
              labelSize="md"
              size="md"
            >
              <Controller
                name={`lpFeeTemplates.${index}.feesEqualisationTreatmentPercentage`}
                control={control}
                render={({ fieldState: { error }, field }) => (
                  <NumberInput
                    {...field}
                    onChange={field.onChange}
                    placeholder="Select equalisation treatment percentage"
                    error={!!error}
                    disabled={hasApprovedFees}
                    helperText={error?.message}
                    decimals={2}
                    showPercentSuffix
                    fullWidth
                  />
                )}
              />
            </FormField>
          )}
          <FormField
            label="Fee start date"
            layout="horizontal"
            breakpoint="md"
            tooltip="This is the date from which annual fees will begin to accrue."
            labelSize="md"
            size="md"
          >
            <Controller
              name={`lpFeeTemplates.${index}.feeStartDate`}
              control={control}
              render={({
                fieldState: { error },
                field: { value, onChange },
              }) => (
                <Tooltip title={templateInUseMessage ?? ''}>
                  <span>
                    <BasicSelect
                      placeholder="Select fee start date"
                      options={feeStartDateOptions}
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      value={value}
                      onChange={onChange}
                      disabled={hasApprovedFees}
                    />
                  </span>
                </Tooltip>
              )}
            />
          </FormField>
          <FormField
            label="Arrears calculation"
            layout="horizontal"
            breakpoint="md"
            labelSize="md"
            size="md"
          >
            <Controller
              name={`lpFeeTemplates.${index}.arrearsCalculation`}
              control={control}
              render={({
                fieldState: { error },
                field: { value, onChange },
              }) => (
                <Tooltip title={templateInUseMessage ?? ''}>
                  <span>
                    <BasicSelect
                      options={arrearsCalculationOptions}
                      fullWidth
                      placeholder="Select arrears calculation"
                      error={!!error}
                      helperText={error?.message}
                      value={value}
                      onChange={onChange}
                      disabled={disabledUntilLaterDate}
                    />
                  </span>
                </Tooltip>
              )}
            />
          </FormField>
          <FeeTemplateFeesTable
            index={index}
            hasApprovedFees={hasApprovedFees}
            hasCommitments={hasCommitments}
            templateInUseMessage={templateInUseMessage}
            setHasEditedTemplateWithCommitment={
              setHasEditedTemplateWithCommitment
            }
          />
          <Row>
            <TooltipButton
              variant="outlined"
              size="large"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(index);
              }}
              disabled={
                hasCommitments || hasApprovedFees || lpFeeTemplates.length < 2
              }
              tooltip={
                hasCommitments || hasApprovedFees
                  ? 'It is not possible to delete a fee template that has commitments or approved fees.'
                  : lpFeeTemplates.length < 2
                  ? 'At least one fee template is required.'
                  : undefined
              }
            >
              Delete fee template
            </TooltipButton>
          </Row>
        </Column>
      </AccordionDetails>
    </Accordion>
  );
};

export default FeeTemplateSection;
